/**
 * Pure builders for the services export — no app imports so they can be
 * exercised outside the browser. The PDF acts as an official-looking,
 * Rhapsody-branded work history (e.g. for visa & immigration paperwork).
 */

export type WorkHistoryRow = {
  date: string;
  type: string;
  engagement: string;
  organization: string;
  venue: string;
};

export type ExcelRow = WorkHistoryRow & {
  start: string;
  end: string;
  timezone: string;
  status: string;
  wage?: number;
};

export type ExportMeta = {
  musicianName: string;
  email?: string;
  periodLabel: string;
  generatedLabel: string;
  organizationsCount: number;
  logo?: { dataURL: string; width: number; height: number };
};

const NAVY = { r: 8, g: 29, b: 58 };
const PAGE_MARGIN = 40;

/**
 * Builds the branded "Work History" PDF and returns the jsPDF document.
 *
 * @param rows services, expected sorted by date ascending
 * @param meta musician & document metadata
 */
export async function buildWorkHistoryPdf(
  rows: WorkHistoryRow[],
  meta: ExportMeta
) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const totalPagesExp = "{total_pages_count_string}";

  const drawHeaderBand = () => {
    doc.setFillColor(NAVY.r, NAVY.g, NAVY.b);
    doc.rect(0, 0, pageW, 92, "F");
    // Thin gold accent under the band, echoing the Rhapsody note mark
    doc.setFillColor(255, 193, 7);
    doc.rect(0, 92, pageW, 2.5, "F");

    if (meta.logo) {
      const h = 30;
      const w = (meta.logo.width / meta.logo.height) * h;
      doc.addImage(meta.logo.dataURL, "PNG", PAGE_MARGIN, 31, w, h);
    } else {
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bolditalic");
      doc.setFontSize(24);
      doc.text("Rhapsody", PAGE_MARGIN, 56);
    }

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("WORK HISTORY", pageW - PAGE_MARGIN, 49, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(255, 255, 255);
    doc.text("Official record of musical services", pageW - PAGE_MARGIN, 63, {
      align: "right",
    });
  };

  drawHeaderBand();

  // ── Identity block ────────────────────────────────────────────────
  let y = 126;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(140, 140, 145);
  doc.text("PREPARED FOR", PAGE_MARGIN, y);
  doc.text("PERIOD", pageW - PAGE_MARGIN - 150, y);
  doc.text("GENERATED", pageW - PAGE_MARGIN, y, { align: "right" });

  y += 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(20, 20, 25);
  doc.text(meta.musicianName, PAGE_MARGIN, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(meta.periodLabel, pageW - PAGE_MARGIN - 150, y - 1);
  doc.text(meta.generatedLabel, pageW - PAGE_MARGIN, y - 1, {
    align: "right",
  });

  if (meta.email) {
    y += 13;
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 128);
    doc.text(meta.email, PAGE_MARGIN, y);
  }

  // ── Summary strip ─────────────────────────────────────────────────
  y += 22;
  const stripH = 54;
  doc.setFillColor(245, 245, 247);
  doc.roundedRect(PAGE_MARGIN, y, pageW - PAGE_MARGIN * 2, stripH, 8, 8, "F");
  const stats = [
    {
      value: `${rows.length}`,
      label: rows.length === 1 ? "SERVICE" : "SERVICES",
    },
    {
      value: `${meta.organizationsCount}`,
      label: meta.organizationsCount === 1 ? "ORGANIZATION" : "ORGANIZATIONS",
    },
    { value: meta.periodLabel, label: "PERIOD COVERED" },
  ];
  const cellW = (pageW - PAGE_MARGIN * 2) / stats.length;
  stats.forEach((s, i) => {
    const cx = PAGE_MARGIN + cellW * i + cellW / 2;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(s.value.length > 8 ? 11 : 16);
    doc.setTextColor(NAVY.r, NAVY.g, NAVY.b);
    doc.text(s.value, cx, y + 25, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 145);
    doc.text(s.label, cx, y + 40, { align: "center" });
  });

  // ── Services table ────────────────────────────────────────────────
  autoTable(doc, {
    startY: y + stripH + 22,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, top: 110, bottom: 56 },
    head: [["Date", "Type", "Engagement", "Organization", "Venue"]],
    body: rows.map((r) => [
      r.date,
      r.type,
      r.engagement,
      r.organization,
      r.venue,
    ]),
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      textColor: [40, 40, 45],
      lineColor: [229, 229, 234],
      lineWidth: { bottom: 0.5 },
    },
    headStyles: {
      fillColor: [NAVY.r, NAVY.g, NAVY.b],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8,
      lineWidth: 0,
    },
    alternateRowStyles: { fillColor: [250, 250, 252] },
    columnStyles: {
      0: { cellWidth: 78 },
      1: { cellWidth: 78 },
      2: { cellWidth: 135 },
      3: { cellWidth: 105 },
      4: { cellWidth: "auto" },
    },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) drawHeaderBand();
      // Footer
      doc.setDrawColor(229, 229, 234);
      doc.setLineWidth(0.5);
      doc.line(PAGE_MARGIN, pageH - 42, pageW - PAGE_MARGIN, pageH - 42);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(140, 140, 145);
      doc.text("Generated by Rhapsody — rhapsody.la", PAGE_MARGIN, pageH - 28);
      doc.text(meta.generatedLabel, pageW / 2, pageH - 28, {
        align: "center",
      });
      doc.text(
        `Page ${data.pageNumber} of ${totalPagesExp}`,
        pageW - PAGE_MARGIN,
        pageH - 28,
        { align: "right" }
      );
    },
  });

  // ── Certification note ────────────────────────────────────────────
  const tableEnd = (doc as any).lastAutoTable?.finalY ?? y + stripH + 22;
  if (tableEnd + 60 < pageH - 56) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 128);
    doc.text(
      `This document lists ${rows.length} musical service${
        rows.length === 1 ? "" : "s"
      } recorded on Rhapsody for ${meta.musicianName}, covering ${
        meta.periodLabel
      }.`,
      PAGE_MARGIN,
      tableEnd + 24,
      { maxWidth: pageW - PAGE_MARGIN * 2 }
    );
  }

  if (typeof (doc as any).putTotalPages === "function") {
    (doc as any).putTotalPages(totalPagesExp);
  }

  return doc;
}

/**
 * Builds the Excel workbook and returns it as a Blob.
 *
 * @param rows services, expected sorted by date ascending
 * @param meta musician & document metadata
 */
export async function buildWorkHistoryExcel(
  rows: ExcelRow[],
  meta: ExportMeta
): Promise<Blob> {
  const mod: any = await import("exceljs");
  const ExcelJS = mod.default ?? mod;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Rhapsody";
  const sheet = workbook.addWorksheet("Work History", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  sheet.mergeCells("A1:J1");
  const title = sheet.getCell("A1");
  title.value = `Rhapsody — Work History for ${meta.musicianName}`;
  title.font = { bold: true, size: 14, color: { argb: "FF081D3A" } };
  sheet.mergeCells("A2:J2");
  const subtitle = sheet.getCell("A2");
  subtitle.value = `${meta.periodLabel} · generated ${meta.generatedLabel}`;
  subtitle.font = { size: 10, color: { argb: "FF8E8E93" } };

  const headers = [
    "Date",
    "Start",
    "End",
    "Timezone",
    "Type",
    "Engagement",
    "Organization",
    "Venue",
    "Status",
    "Wage",
  ];
  const headerRow = sheet.getRow(4);
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF081D3A" },
    };
    cell.alignment = { vertical: "middle" };
  });
  headerRow.height = 22;

  rows.forEach((r, i) => {
    const row = sheet.getRow(5 + i);
    row.values = [
      r.date,
      r.start,
      r.end,
      r.timezone,
      r.type,
      r.engagement,
      r.organization,
      r.venue,
      r.status,
      r.wage ?? "",
    ];
    if (typeof r.wage === "number") {
      row.getCell(10).numFmt = '"$"#,##0.00';
    }
  });

  const widths = [20, 10, 10, 10, 16, 32, 24, 36, 12, 12];
  widths.forEach((w, i) => {
    sheet.getColumn(i + 1).width = w;
  });
  sheet.autoFilter = { from: "A4", to: `J${4 + rows.length}` };

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}
